import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import ViewList from '@mui/icons-material/ViewList';
import { Card, Typography, IconButton } from '@totalsoft/rocket-ui';
import { TableBody, TableRow, TableCell, TableHead, Box } from '@mui/material';
import { getErrors } from '@totalsoft/pure-validations-react';
import PropertyUserGroupItemComponent from './PropertyUserGroupItemComponent';
import { StyledHeaderCell, StyledTable, StyledGrayIcon, StyledTableRow } from 'components/common/Styles';
import { emptyString } from 'utils/constants';
import { Add, Save } from '@mui/icons-material';
import { ADMIN_PROPERTIES_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const PropertyUserGroupsListComponent = ({
  propertyUserGroups,
  userGroups,
  onPropertyChanged,
  onSave,
  onDelete,
  onAdd,
  onCancel,
  onEdit,
  validation,
  saveDisabled,
  isNewProperty
}) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_PROPERTIES_WRITE);

  return (
    <Card
      icon={ViewList}
      iconColor='primary'
      title={t('Property.Security')}
      actions={
        <>
          <IconButton type='add' fontSize='medium' color='transparent' title={t('Tooltips.Add')} onClick={onAdd} disabled={!canWrite}>
            <Add />
          </IconButton>
          <IconButton
            type='save'
            fontSize='medium'
            color='transparent'
            title={t('SaveChanges')}
            onClick={onSave}
            disabled={!canWrite || (validation |> getErrors) !== emptyString || saveDisabled || !propertyUserGroups.changed}
          >
            <Save />
          </IconButton>
        </>
      }
      subheader={<Typography color='error'>{validation |> getErrors}</Typography>}
    >
      <Prompt when={propertyUserGroups.changed} message={t('Property.LeavingWithoutSavingProperty')} />
      <StyledTable>
        <TableHead>
          <TableRow>
            <StyledHeaderCell sx={{ w: 1 / 2 }}>{t('Property.UserGroup')}</StyledHeaderCell>
            <StyledHeaderCell>{t('AllowRead')}</StyledHeaderCell>
            <StyledHeaderCell>{t('AllowWrite')}</StyledHeaderCell>
            <StyledHeaderCell align='right'>{t('Property.Actions')}</StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <>
            {!isNewProperty && propertyUserGroups.values.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={4}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('ResultsNotFound')}</Typography>
                  </Box>
                </TableCell>
              </StyledTableRow>
            )}
            {propertyUserGroups.values.map((item, idx) => {
              return (
                <PropertyUserGroupItemComponent
                  propertyUserGroup={item}
                  index={idx}
                  key={idx}
                  onPropertyChanged={onPropertyChanged}
                  userGroups={userGroups}
                  validation={validation}
                  onDelete={onDelete}
                  onCancel={onCancel}
                  onEdit={onEdit}
                />
              );
            })}
          </>
        </TableBody>
      </StyledTable>
    </Card>
  );
};

PropertyUserGroupsListComponent.propTypes = {
  propertyUserGroups: PropTypes.object.isRequired,
  userGroups: PropTypes.array.isRequired,
  onPropertyChanged: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  isNewProperty: PropTypes.bool.isRequired
};

export default PropertyUserGroupsListComponent;
